/**
 * Text-to-pay — public pay page at `/pay/<pl_token>` (migration 0098).
 *
 * No customer sign-in: the token in the URL is the auth. Fetches the link
 * summary from GET /api/customer/pay/:token, lets the customer add a tip when
 * the tenant has tips enabled (none / preset % chips / custom), creates the
 * PaymentIntent via POST /api/customer/pay/:token/intent and mounts Stripe's
 * Payment Element (same pattern as PayBookingPanel — Apple Pay / Google Pay
 * appear automatically because the server enables automatic_payment_methods).
 *
 * States: loading · invalid (404) · expired (410) · cancelled · paid · open
 * (tip → pay) · success. A wallet / 3DS redirect returns to this URL with
 * `?redirect_status=succeeded`, which renders the success state directly.
 */
import { type Stripe, loadStripe } from '@stripe/stripe-js';
import {
  type CSSProperties,
  type ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { BrandedShell } from '../components/BrandedShell';
import { CustomerApiError, customerApi } from '../customer_api';
import { formatMoney } from '../lib/format';
import { getStripePublishableKey } from '../lib/stripe_env';

export interface PayLinkView {
  status: 'open' | 'paid' | 'expired' | 'cancelled';
  purpose: 'booking_balance' | 'booking_deposit' | 'pos' | 'custom';
  purposeLabel: string;
  description: string | null;
  amountMinor: number;
  currency: string;
  tipMinor: number;
  paidAt: string | null;
  expiresAt: string;
  accountName: string;
  customerFirstName: string | null;
  pet: { name: string } | null;
  booking: { type: string; status: string; checkIn: string | null; checkout: string | null } | null;
  tips: { enabled: boolean; presets: number[]; allowCustom: boolean };
}

interface IntentResponse {
  client_secret: string;
  payment_intent_id: string;
  stripe_account_id: string;
  amount_minor: number;
  tip_minor: number;
  total_minor: number;
  currency: string;
}

type LoadState =
  | { kind: 'loading' }
  | { kind: 'invalid' }
  | { kind: 'expired' }
  | { kind: 'error'; message: string }
  | { kind: 'ready'; view: PayLinkView };

type PayPhase = 'idle' | 'creating_intent' | 'element_ready' | 'submitting' | 'paid' | 'error';

type TipChoice = { kind: 'none' } | { kind: 'preset'; pct: number } | { kind: 'custom' };

const wrapperStyle: CSSProperties = {
  minHeight: '100dvh',
  background: 'var(--background, oklch(0.98 0.005 80))',
  color: 'var(--foreground, oklch(0.19 0.012 75))',
};
const cardStyle: CSSProperties = {
  maxWidth: 480,
  margin: '0 auto',
  padding: '2rem 1.25rem 3rem',
};
const panelStyle: CSSProperties = {
  padding: '1.25rem',
  background: 'var(--surface, white)',
  borderRadius: 12,
  border: '1px solid var(--border, oklch(0.915 0.005 80))',
};
const labelStyle: CSSProperties = {
  fontSize: '0.7rem',
  textTransform: 'uppercase',
  letterSpacing: '0.14em',
  color: 'var(--muted-foreground, oklch(0.52 0.008 75))',
  marginBottom: '0.5rem',
};
const primaryButton: CSSProperties = {
  display: 'block',
  width: '100%',
  padding: '0.85rem 1rem',
  borderRadius: 10,
  border: 'none',
  background: 'var(--primary, oklch(0.43 0.18 263))',
  color: 'white',
  fontSize: '0.95rem',
  fontWeight: 500,
  cursor: 'pointer',
};
const mutedText: CSSProperties = {
  margin: 0,
  fontSize: '0.9rem',
  color: 'var(--muted-foreground, oklch(0.52 0.008 75))',
};

function chipStyle(active: boolean): CSSProperties {
  return {
    padding: '0.55rem 0.9rem',
    borderRadius: 999,
    fontSize: '0.9rem',
    fontWeight: 500,
    cursor: 'pointer',
    border: active
      ? '1px solid var(--primary, oklch(0.43 0.18 263))'
      : '1px solid var(--border, oklch(0.915 0.005 80))',
    background: active ? 'var(--primary, oklch(0.43 0.18 263))' : 'var(--surface, white)',
    color: active ? 'white' : 'var(--foreground, oklch(0.19 0.012 75))',
  };
}

function formatDate(iso: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

export function tipForChoice(choice: TipChoice, amountMinor: number, customInput: string): number {
  if (choice.kind === 'none') return 0;
  if (choice.kind === 'preset') return Math.round((amountMinor * choice.pct) / 100);
  const minor = Math.round(Number.parseFloat(customInput || '0') * 100);
  return Number.isFinite(minor) && minor > 0 ? minor : 0;
}

export function PayLinkPage() {
  const { token = '' } = useParams<{ token: string }>();
  const [search] = useSearchParams();
  const redirectedSuccess = search.get('redirect_status') === 'succeeded';

  const [load, setLoad] = useState<LoadState>({ kind: 'loading' });
  const [tipChoice, setTipChoice] = useState<TipChoice>({ kind: 'none' });
  const [customTip, setCustomTip] = useState('');
  const [phase, setPhase] = useState<PayPhase>(redirectedSuccess ? 'paid' : 'idle');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [intent, setIntent] = useState<IntentResponse | null>(null);

  const stripeRef = useRef<Stripe | null>(null);
  // biome-ignore lint/suspicious/noExplicitAny: StripeElements isn't worth re-typing here.
  const elementsRef = useRef<any>(null);
  const mountNodeRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    let cancelled = false;
    customerApi<PayLinkView>(`/api/customer/pay/${encodeURIComponent(token)}`)
      .then((view) => {
        if (!cancelled) setLoad({ kind: 'ready', view });
      })
      .catch((err) => {
        if (cancelled) return;
        if (err instanceof CustomerApiError && err.status === 404) setLoad({ kind: 'invalid' });
        else if (err instanceof CustomerApiError && err.status === 410)
          setLoad({ kind: 'expired' });
        else setLoad({ kind: 'error', message: 'Could not load this payment. Please try again.' });
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  const fail = useCallback((msg: string) => {
    setPhase('error');
    setErrorMsg(msg);
  }, []);

  const view = load.kind === 'ready' ? load.view : null;
  const tipMinor = view ? tipForChoice(tipChoice, view.amountMinor, customTip) : 0;
  const totalMinor = view ? view.amountMinor + tipMinor : 0;

  const beginPayment = useCallback(async () => {
    if (!view) return;
    if (phase === 'creating_intent' || phase === 'submitting') return;
    const publishableKey = getStripePublishableKey();
    if (!publishableKey) {
      fail('Online payment is not available right now. Please contact us to pay another way.');
      return;
    }
    setPhase('creating_intent');
    setErrorMsg(null);

    let res: IntentResponse;
    try {
      res = await customerApi<IntentResponse>(
        `/api/customer/pay/${encodeURIComponent(token)}/intent`,
        {
          method: 'POST',
          body: JSON.stringify({ tipMinor }),
        },
      );
    } catch (err) {
      const code =
        err instanceof CustomerApiError && err.body && typeof err.body === 'object'
          ? (err.body as { error?: string }).error
          : undefined;
      if (code === 'link_not_open') fail('This link has already been used or cancelled.');
      else if (code === 'link_expired')
        fail('This link has expired — please ask us for a new one.');
      else if (code === 'tips_disabled' || code === 'tip_too_large')
        fail('Please choose a smaller tip.');
      else fail('Could not start payment. Please try again.');
      return;
    }
    setIntent(res);

    let stripe: Stripe | null;
    try {
      stripe = await loadStripe(publishableKey, { stripeAccount: res.stripe_account_id });
    } catch {
      fail('Could not load the payment form. Please refresh and try again.');
      return;
    }
    if (!stripe) {
      fail('Could not load the payment form. Please refresh and try again.');
      return;
    }
    stripeRef.current = stripe;
    setPhase('element_ready');
  }, [view, phase, token, tipMinor, fail]);

  useEffect(() => {
    if (phase !== 'element_ready') return;
    if (!intent || !stripeRef.current || !mountNodeRef.current) return;
    const elements = stripeRef.current.elements({
      clientSecret: intent.client_secret,
      appearance: { theme: 'stripe' },
    });
    elementsRef.current = elements;
    // Default (no `wallets: never`) so Apple Pay / Google Pay show when eligible.
    const paymentElement = elements.create('payment', { layout: 'tabs' });
    paymentElement.mount(mountNodeRef.current);
    return () => {
      try {
        paymentElement.unmount();
      } catch {
        // already torn down
      }
    };
  }, [intent, phase]);

  const submitPayment = useCallback(async () => {
    if (!stripeRef.current || !elementsRef.current) {
      fail('Payment not ready. Please try again.');
      return;
    }
    setPhase('submitting');
    setErrorMsg(null);
    const { error, paymentIntent } = await stripeRef.current.confirmPayment({
      elements: elementsRef.current,
      redirect: 'if_required',
      confirmParams: { return_url: `${window.location.origin}/pay/${token}` },
    });
    if (error) {
      fail(error.message ?? 'Payment failed. Please try again.');
      return;
    }
    if (!paymentIntent) {
      fail('Payment did not complete. Please try again.');
      return;
    }
    setPhase('paid');
  }, [token, fail]);

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  let content: ReactNode;

  if (phase === 'paid') {
    content = (
      <div style={panelStyle} role="status">
        <h1 style={{ fontSize: '1.4rem', fontWeight: 600, margin: '0 0 0.5rem' }}>
          Payment received
        </h1>
        <p style={mutedText}>
          Thank you{view?.customerFirstName ? `, ${view.customerFirstName}` : ''}.
          {intent ? ` We've taken ${formatMoney(intent.total_minor, intent.currency)}` : ''}
          {intent && intent.tip_minor > 0
            ? ` including a ${formatMoney(intent.tip_minor, intent.currency)} tip — much appreciated.`
            : intent
              ? '.'
              : ''}
          {view ? ` ${view.accountName} has been notified.` : ''}
        </p>
      </div>
    );
  } else if (load.kind === 'loading') {
    content = <p style={mutedText}>Loading your payment…</p>;
  } else if (load.kind === 'invalid') {
    content = (
      <div style={panelStyle}>
        <h1 style={{ fontSize: '1.4rem', fontWeight: 600, margin: '0 0 0.5rem' }}>
          Link not recognised
        </h1>
        <p style={mutedText}>This payment link isn't valid. Please ask us to send you a new one.</p>
      </div>
    );
  } else if (load.kind === 'expired') {
    content = (
      <div style={panelStyle}>
        <h1 style={{ fontSize: '1.4rem', fontWeight: 600, margin: '0 0 0.5rem' }}>Link expired</h1>
        <p style={mutedText}>Payment links last 7 days. Please ask us for a fresh link.</p>
      </div>
    );
  } else if (load.kind === 'error') {
    content = (
      <div style={panelStyle}>
        <p style={{ ...mutedText, color: 'var(--destructive, oklch(0.55 0.15 30))' }}>
          {load.message}
        </p>
      </div>
    );
  } else if (view && view.status === 'paid') {
    content = (
      <div style={panelStyle} role="status">
        <h1 style={{ fontSize: '1.4rem', fontWeight: 600, margin: '0 0 0.5rem' }}>Already paid</h1>
        <p style={mutedText}>
          {formatMoney(view.amountMinor + view.tipMinor, view.currency)} was paid
          {view.paidAt ? ` on ${formatDate(view.paidAt)}` : ''}. Nothing more to do — thank you.
        </p>
      </div>
    );
  } else if (view && view.status === 'cancelled') {
    content = (
      <div style={panelStyle}>
        <h1 style={{ fontSize: '1.4rem', fontWeight: 600, margin: '0 0 0.5rem' }}>
          Link cancelled
        </h1>
        <p style={mutedText}>
          {view.accountName} has cancelled this payment link. If you still need to pay, please ask
          them for a new one.
        </p>
      </div>
    );
  } else if (view) {
    const checkIn = formatDate(view.booking?.checkIn ?? null);
    const checkout = formatDate(view.booking?.checkout ?? null);
    const tipsOn = view.tips.enabled;
    content = (
      <>
        <div style={{ ...panelStyle, marginBottom: '1rem' }}>
          <div style={labelStyle}>Payment to {view.accountName}</div>
          <p style={{ margin: '0 0 0.25rem', fontSize: '1rem' }}>
            {view.customerFirstName ? `Hi ${view.customerFirstName}, ` : ''}you're paying{' '}
            {view.purposeLabel}.
          </p>
          {view.pet && checkIn && (
            <p style={mutedText}>
              {view.pet.name} · {checkIn}
              {checkout ? ` – ${checkout}` : ''}
            </p>
          )}
          <p
            className="num"
            style={{ margin: '0.75rem 0 0', fontSize: '1.75rem', fontWeight: 600 }}
          >
            {formatMoney(view.amountMinor, view.currency)}
          </p>
        </div>

        {tipsOn && phase === 'idle' && (
          <div style={{ ...panelStyle, marginBottom: '1rem' }}>
            <div style={labelStyle}>Add a tip for the team?</div>
            <div
              style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}
              role="group"
              aria-label="Tip"
            >
              <button
                type="button"
                style={chipStyle(tipChoice.kind === 'none')}
                aria-pressed={tipChoice.kind === 'none'}
                onClick={() => setTipChoice({ kind: 'none' })}
              >
                No tip
              </button>
              {view.tips.presets.map((pct) => (
                <button
                  key={pct}
                  type="button"
                  style={chipStyle(tipChoice.kind === 'preset' && tipChoice.pct === pct)}
                  aria-pressed={tipChoice.kind === 'preset' && tipChoice.pct === pct}
                  onClick={() => setTipChoice({ kind: 'preset', pct })}
                >
                  <span className="num">{pct}%</span>{' '}
                  <span className="num" style={{ opacity: 0.75 }}>
                    ({formatMoney(Math.round((view.amountMinor * pct) / 100), view.currency)})
                  </span>
                </button>
              ))}
              {view.tips.allowCustom && (
                <button
                  type="button"
                  style={chipStyle(tipChoice.kind === 'custom')}
                  aria-pressed={tipChoice.kind === 'custom'}
                  onClick={() => setTipChoice({ kind: 'custom' })}
                >
                  Custom
                </button>
              )}
            </div>
            {tipChoice.kind === 'custom' && (
              <div style={{ marginTop: '0.75rem' }}>
                <label
                  htmlFor="custom-tip"
                  style={{ ...mutedText, display: 'block', marginBottom: 4 }}
                >
                  Tip amount ({view.currency})
                </label>
                <input
                  id="custom-tip"
                  type="number"
                  min="0"
                  step="0.01"
                  inputMode="decimal"
                  className="num"
                  value={customTip}
                  onChange={(e) => setCustomTip(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '0.6rem 0.75rem',
                    borderRadius: 8,
                    border: '1px solid var(--border, oklch(0.915 0.005 80))',
                    fontSize: '1rem',
                  }}
                />
              </div>
            )}
          </div>
        )}

        <div style={panelStyle}>
          {tipMinor > 0 && (
            <p style={{ ...mutedText, marginBottom: '0.75rem' }}>
              {formatMoney(view.amountMinor, view.currency)} +{' '}
              <span className="num">{formatMoney(tipMinor, view.currency)}</span> tip
            </p>
          )}
          {phase === 'idle' && (
            <button type="button" onClick={() => void beginPayment()} style={primaryButton}>
              Pay {formatMoney(totalMinor, view.currency)}
            </button>
          )}
          {phase === 'creating_intent' && <p style={mutedText}>Preparing payment…</p>}
          {(phase === 'element_ready' || phase === 'submitting') && (
            <>
              <div ref={mountNodeRef} data-testid="payment-element" />
              <button
                type="button"
                onClick={() => void submitPayment()}
                disabled={phase === 'submitting'}
                style={{
                  ...primaryButton,
                  marginTop: '1rem',
                  opacity: phase === 'submitting' ? 0.6 : 1,
                  cursor: phase === 'submitting' ? 'not-allowed' : 'pointer',
                }}
              >
                {phase === 'submitting'
                  ? 'Processing…'
                  : `Pay now ${formatMoney(intent?.total_minor ?? totalMinor, view.currency)}`}
              </button>
            </>
          )}
          {phase === 'error' && (
            <>
              <p
                style={{
                  ...mutedText,
                  color: 'var(--destructive, oklch(0.55 0.15 30))',
                  marginBottom: '0.75rem',
                }}
              >
                {errorMsg ?? 'Payment failed.'}
              </p>
              <button
                type="button"
                onClick={() => {
                  setPhase('idle');
                  setErrorMsg(null);
                }}
                style={{
                  ...primaryButton,
                  background: 'var(--surface, white)',
                  color: 'var(--foreground, oklch(0.19 0.012 75))',
                  border: '1px solid var(--border, oklch(0.915 0.005 80))',
                }}
              >
                Try again
              </button>
            </>
          )}
          <p style={{ ...mutedText, marginTop: '0.75rem', fontSize: '0.75rem' }}>
            Secure payment powered by Stripe. Link expires {formatDate(view.expiresAt)}.
          </p>
        </div>
      </>
    );
  } else {
    content = <p style={mutedText}>Loading…</p>;
  }

  return (
    <BrandedShell>
      <div style={wrapperStyle}>
        <div style={cardStyle}>{content}</div>
      </div>
    </BrandedShell>
  );
}
